import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { API } from '@/App';
import DashboardLayout from '@/components/DashboardLayout';
import useTenantFeatures from '@/hooks/useTenantFeatures';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend, ReferenceLine
} from 'recharts';
import {
  MessageSquare, TrendingUp, TrendingDown, AlertTriangle,
  CheckCircle, Clock, Filter, Building2, BarChart3,
  Target, RefreshCw, Activity, ArrowRight, X as XIcon,
  Sparkles, Send, Download
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { cleanQuestionLabel } from '@/lib/answerFormatting';
import MSInsightsSection from '@/components/mystery_shopping/MSInsightsSection';

const COLORS = {
  blue: '#1A3E6F', red: '#D62B27', green: '#4EBC87', yellow: '#F1D511',
  teal: '#5BC4BF', purple: '#603795', grey: '#64748B', orange: '#F97316'
};
const SENTIMENT_COLORS = [COLORS.green, COLORS.yellow, COLORS.red];
const PRIORITY_COLORS = { low: COLORS.grey, medium: COLORS.teal, high: COLORS.orange, critical: COLORS.red };

// Color helper for an NPS index value: positive = green, 0–30 = orange, negative = red.
const npsColorClass = (n) => {
  if (n == null || isNaN(n)) return 'text-[#0F172A]';
  if (n < 0) return 'text-[#D62B27]';
  if (n <= 30) return 'text-[#F97316]';
  return 'text-[#4EBC87]';
};
const formatNpsIndex = (n) => {
  if (n == null || isNaN(n)) return '–';
  const v = Math.round(n);
  return v > 0 ? `+${v}` : `${v}`;
};
// Bar color for a satisfaction score (1–5): 1–2 red, 3 orange, 4–5 green.
const satScoreColor = (s) => {
  const n = Number(s);
  if (n <= 2) return COLORS.red;
  if (n === 3) return COLORS.orange;
  return COLORS.green;
};

export default function Dashboard() {
  const { user, hasCases, hasGapLibrary } = useAuth();
  const tenantFeatures = useTenantFeatures();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterBranch, setFilterBranch] = useState('all');
  const [filterRegion, setFilterRegion] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterDateRange, setFilterDateRange] = useState('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [usage, setUsage] = useState(null);

  const dateParams = useMemo(() => {
    // Format as YYYY-MM-DD using LOCAL calendar date (not UTC).
    // Using d.toISOString() converts to UTC first, which for users east of UTC
    // (e.g., Asia/Manila +08:00) shifts the date back one day and causes the
    // backend filter to miss all responses submitted "today" in local time.
    const toStr = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    if (filterDateRange === 'all') return {};
    if (filterDateRange === 'custom') {
      if (customDateFrom && customDateTo) return { date_from: customDateFrom, date_to: customDateTo };
      return {};
    }
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let from;
    let to = startOfDay;

    switch (filterDateRange) {
      case 'today':
        from = startOfDay; break;
      case 'yesterday': {
        const y = new Date(startOfDay); y.setDate(y.getDate() - 1);
        from = y; to = y; break;
      }
      case '7d':
        from = new Date(startOfDay); from.setDate(from.getDate() - 6); break;
      case '30d':
        from = new Date(startOfDay); from.setDate(from.getDate() - 29); break;
      case '3m':
        from = new Date(startOfDay); from.setMonth(from.getMonth() - 3); break;
      case '6m':
        from = new Date(startOfDay); from.setMonth(from.getMonth() - 6); break;
      case '12m':
        from = new Date(startOfDay); from.setMonth(from.getMonth() - 12); break;
      case 'this_month':
        from = new Date(now.getFullYear(), now.getMonth(), 1); break;
      case 'last_month':
        from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        to = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'this_quarter': {
        const q = Math.floor(now.getMonth() / 3);
        from = new Date(now.getFullYear(), q * 3, 1); break;
      }
      case 'last_quarter': {
        const q = Math.floor(now.getMonth() / 3);
        from = new Date(now.getFullYear(), (q - 1) * 3, 1);
        to = new Date(now.getFullYear(), q * 3, 0);
        break;
      }
      case 'this_year':
        from = new Date(now.getFullYear(), 0, 1); break;
      default:
        return {};
    }
    return { date_from: toStr(from), date_to: toStr(to) };
  }, [filterDateRange, customDateFrom, customDateTo]);

  useEffect(() => { fetchAnalytics(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBranch, filterRegion, filterCategory, filterDateRange, customDateFrom, customDateTo]);

  useEffect(() => {
    // Only tenant users have meaningful usage; platform_admin without tenant_id is excluded
    if (!user?.tenant_id) return;
    fetch(`${API}/tenant/usage`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setUsage(d); })
      .catch(() => {});
  }, [user?.tenant_id]);

  const fetchAnalytics = async () => {
    try {
      const params = new URLSearchParams();
      if (filterBranch !== 'all') params.append('branch_id', filterBranch);
      if (filterRegion !== 'all') params.append('region_id', filterRegion);
      if (filterCategory !== 'all') params.append('category', filterCategory);
      if (dateParams.date_from) params.append('date_from', dateParams.date_from);
      if (dateParams.date_to) params.append('date_to', dateParams.date_to);
      const url = `${API}/dashboard/analytics${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        setData(await response.json());
      } else {
        toast.error('Failed to load analytics');
      }
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilterBranch('all');
    setFilterRegion('all');
    setFilterCategory('all');
    setFilterDateRange('all');
    setCustomDateFrom('');
    setCustomDateTo('');
  };

  const hasActiveFilters = filterBranch !== 'all' || filterRegion !== 'all' || filterCategory !== 'all' || filterDateRange !== 'all';

  const getRoleTitle = () => 'Dashboard';

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6 animate-pulse" data-testid="dashboard-loading">
          <div className="h-10 bg-gray-200 rounded w-64 mb-2" />
          <div className="h-5 bg-gray-200 rounded w-96" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <div key={i} className="h-28 bg-gray-200 rounded-lg" />)}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-72 bg-gray-200 rounded-lg" />
            <div className="h-72 bg-gray-200 rounded-lg" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout>
        <div className="text-center py-20" data-testid="dashboard-empty">
          <BarChart3 className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#0F172A] font-['Montserrat']">No Data Available</h2>
          <p className="text-[#64748B] mt-1">Start collecting feedback to see your dashboard analytics.</p>
        </div>
      </DashboardLayout>
    );
  }

  const { response_stats, case_status, case_priority, top_gaps, branch_comparison,
    pending_actions, issue_trends, action_completion, nps_trend, repeat_issues,
    regions, categories, branches } = data;

  const sentimentData = [
    { name: 'Positive', value: response_stats.sentiment_split.positive },
    { name: 'Neutral', value: response_stats.sentiment_split.neutral },
    { name: 'Negative', value: response_stats.sentiment_split.negative },
  ].filter(d => d.value > 0);

  const totalCases = case_status.open + case_status.in_progress + case_status.closed;
  const openCases = case_status.open + case_status.in_progress;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="dashboard-page">
        {/* Plan Usage Banner — free_nps only */}
        {usage?.plan_type === 'free_nps' && usage?.monthly_response_limit ? (() => {
          const used = usage.current_month_responses || 0;
          const limit = usage.monthly_response_limit;
          const pct = Math.min(100, Math.round((used / limit) * 100));
          const atLimit = used >= limit;
          const atWarn = !atLimit && used >= Math.floor(limit * 0.8);
          if (!atLimit && !atWarn) return null;
          const bg = atLimit ? 'bg-[#FEF2F2] border-[#FCA5A5]' : 'bg-[#FFFBEB] border-[#FCD34D]';
          const textColor = atLimit ? 'text-[#991B1B]' : 'text-[#92400E]';
          const iconColor = atLimit ? 'text-[#DC2626]' : 'text-[#D97706]';
          const title = atLimit
            ? 'You have reached your satisFIND® free plan limit'
            : `You've used ${used} of ${limit} free responses this month`;
          const body = atLimit
            ? 'New survey submissions are blocked until next month. Upgrade to continue collecting feedback immediately.'
            : 'You are approaching your monthly limit on the NPS® Free plan. Consider upgrading to keep collecting feedback without interruption.';
          return (
            <div className={`border rounded-lg p-4 ${bg}`} data-testid="plan-usage-banner">
              <div className="flex items-start gap-3">
                <AlertTriangle className={`w-5 h-5 shrink-0 mt-0.5 ${iconColor}`} />
                <div className="flex-1 min-w-0">
                  <p className={`font-semibold text-sm ${textColor} font-['Montserrat']`} data-testid="plan-usage-title">{title}</p>
                  <p className={`text-xs mt-1 ${textColor}/80`}>{body}</p>
                  <div className="mt-3 h-1.5 bg-white/60 rounded-full overflow-hidden">
                    <div className={`h-full ${atLimit ? 'bg-[#DC2626]' : 'bg-[#D97706]'}`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className={`text-[11px] ${textColor}/70`} data-testid="plan-usage-count">{used}/{limit} responses</span>
                    <a href="/pricing" className={`text-xs font-semibold ${textColor} hover:underline`} data-testid="plan-usage-upgrade-link">Upgrade plan →</a>
                  </div>
                </div>
              </div>
            </div>
          );
        })() : null}
        {/* Header Row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0F172A] font-['Montserrat']" data-testid="dashboard-title">
              {getRoleTitle()}
            </h1>
            <p className="text-[#64748B] mt-1 text-sm">
              Welcome back, {user?.name?.split(' ')[0]}. Here are your operational insights.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setFiltersVisible(!filtersVisible)} data-testid="toggle-filters-btn"
              className={hasActiveFilters ? 'border-[#1A3E6F] text-[#1A3E6F]' : ''}>
              <Filter className="w-4 h-4 mr-1" />
              Filters {hasActiveFilters && <Badge className="ml-1 bg-[#1A3E6F] text-white text-[10px] px-1.5">ON</Badge>}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { setLoading(true); fetchAnalytics(); }} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Filters Bar */}
        {filtersVisible && (
          <Card className="border-[#E2E8F0] shadow-sm" data-testid="filters-card">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[160px]">
                  <label className="text-xs text-[#64748B] font-medium mb-1 block">Date Range</label>
                  <Select value={filterDateRange} onValueChange={setFilterDateRange}>
                    <SelectTrigger className="h-9" data-testid="filter-date"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="today">Today</SelectItem>
                      <SelectItem value="yesterday">Yesterday</SelectItem>
                      <SelectItem value="7d">Last 7 Days</SelectItem>
                      <SelectItem value="30d">Last 30 Days</SelectItem>
                      <SelectItem value="3m">Last 3 Months</SelectItem>
                      <SelectItem value="6m">Last 6 Months</SelectItem>
                      <SelectItem value="12m">Last 12 Months</SelectItem>
                      <SelectItem value="this_month">This Month</SelectItem>
                      <SelectItem value="last_month">Last Month</SelectItem>
                      <SelectItem value="this_quarter">This Quarter</SelectItem>
                      <SelectItem value="last_quarter">Last Quarter</SelectItem>
                      <SelectItem value="this_year">This Year</SelectItem>
                      <SelectItem value="all">All Time</SelectItem>
                      <SelectItem value="custom">Custom Range</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {filterDateRange === 'custom' && (
                  <>
                    <div className="min-w-[140px]">
                      <label className="text-xs text-[#64748B] font-medium mb-1 block">From</label>
                      <input
                        type="date"
                        value={customDateFrom}
                        onChange={(e) => setCustomDateFrom(e.target.value)}
                        className="h-9 w-full rounded-md border border-[#E2E8F0] bg-white px-2.5 text-sm text-[#0F172A] focus:outline-none focus:ring-1 focus:ring-[#1A3E6F]"
                        data-testid="filter-date-from"
                      />
                    </div>
                    <div className="min-w-[140px]">
                      <label className="text-xs text-[#64748B] font-medium mb-1 block">To</label>
                      <input
                        type="date"
                        value={customDateTo}
                        onChange={(e) => setCustomDateTo(e.target.value)}
                        className="h-9 w-full rounded-md border border-[#E2E8F0] bg-white px-2.5 text-sm text-[#0F172A] focus:outline-none focus:ring-1 focus:ring-[#1A3E6F]"
                        data-testid="filter-date-to"
                      />
                    </div>
                  </>
                )}
                {user?.role !== 'branch_manager' && (
                  <div className="min-w-[150px]">
                    <label className="text-xs text-[#64748B] font-medium mb-1 block">Branch</label>
                    <Select value={filterBranch} onValueChange={setFilterBranch}>
                      <SelectTrigger className="h-9" data-testid="filter-branch"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Branches</SelectItem>
                        {branches?.map(b => <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {(user?.role === 'tenant_admin' || user?.role === 'regional_manager') && regions?.length > 0 && (
                  <div className="min-w-[140px]">
                    <label className="text-xs text-[#64748B] font-medium mb-1 block">Region</label>
                    <Select value={filterRegion} onValueChange={setFilterRegion}>
                      <SelectTrigger className="h-9" data-testid="filter-region"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Regions</SelectItem>
                        {regions.map(r => <SelectItem key={r.region_id} value={r.region_id}>{r.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {categories?.length > 0 && (
                  <div className="min-w-[140px]">
                    <label className="text-xs text-[#64748B] font-medium mb-1 block">Category</label>
                    <Select value={filterCategory} onValueChange={setFilterCategory}>
                      <SelectTrigger className="h-9" data-testid="filter-category"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-[#D62B27] h-9">
                    <XIcon className="w-3 h-3 mr-1" /> Clear
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* KPI Row — Total Responses (narrow) | NPS Score (narrow) | NPS Breakdown (wide) */}
        {(() => {
          const npsRaw = Number(response_stats?.nps_breakdown?.nps_score ?? 0);
          const npsInt = Math.round(npsRaw);
          const npsLabel = npsInt > 0 ? `+${npsInt}` : `${npsInt}`;
          return (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
              <div className="md:col-span-3">
                <KPICard icon={MessageSquare} iconBg="bg-[#1A3E6F]/10" iconColor="text-[#1A3E6F]"
                  value={response_stats.total_responses} label="Total Responses" testId="kpi-responses" />
              </div>
              <div className="md:col-span-3">
                <KPICard icon={Target} iconBg="bg-[#5BC4BF]/15" iconColor="text-[#5BC4BF]"
                  value={npsLabel} label="Net Promoter® Score" testId="kpi-nps"
                  onClick={() => navigate('/responses?focus=nps')}
                  sublabel="View details →"
                  trend={npsInt > 0 ? 'up' : npsInt < 0 ? 'down' : null} />
              </div>
              <div className="md:col-span-6">
                <NpsBreakdownKpiCard
                  breakdown={response_stats?.nps_breakdown}
                  total={response_stats?.total_responses || 0}
                  onSegmentClick={(category) => navigate(`/responses?nps_category=${category}`)}
                />
              </div>
            </div>
          );
        })()}

        {/* Cases KPI row — only when the tenant has the cases module
            AND the cases_widgets feature flag is on (Fix 2 — hidden by
            default; flip via tenants.features.cases_widgets in DB) */}
        {hasCases && tenantFeatures.cases_widgets && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <KPICard icon={AlertTriangle} iconBg="bg-[#D62B27]/10" iconColor="text-[#D62B27]"
              value={openCases} label="Open Cases" testId="kpi-open-cases" />
            <KPICard icon={CheckCircle} iconBg="bg-[#4EBC87]/15" iconColor="text-[#4EBC87]"
              value={`${action_completion.closure_rate}%`} label="Resolution Rate" testId="kpi-resolution" />
          </div>
        )}

        {/* Mystery Shopping summary card (Fix 2) — separate from VOX metrics
            so MS averages are never blended into the standard NPS / sentiment
            widgets. Renders only when the tenant has at least one mystery
            shopping survey configured. */}
        {tenantFeatures.has_mystery_shopping && (
          <MysteryShoppingSummaryCard
            published30d={tenantFeatures.mystery_published_30d}
            publishedTotal={tenantFeatures.mystery_published_total}
            onView={() => navigate('/reports')}
          />
        )}

        {/* Client Experience V2 — Ask satisFIND V1. Text Q&A grounded
            in the tenant's authorized FIND data. Cited Response IDs are
            clickable deep-links to /reports/{public_id}.

            Jun 2026 — gated by the platform product feature
            `ask_satisfind`. Available today for Mystery Shopping tenants
            only (MS-context implementation). Pure NPS / CSAT / VOX tenants
            no longer see an empty, non-functional card. */}
        {tenantFeatures.product_features?.ask_satisfind && (
          <AskSatisFindCard
            dateParams={dateParams}
            branchIds={filterBranch !== 'all' ? filterBranch : undefined}
          />
        )}

        {/* Client Experience V2 — Management Insights section. Every
            insight is evidence-based; every Response ID chip is a
            deep-link to /reports/{public_id} in a new tab.

            Jun 2026 — gated by the platform product feature
            `management_insights`. This is a Mystery-Shopping-only surface
            (MSInsightsSection). Pure NPS / CSAT / VOX tenants no longer see
            an empty Management Insights card. */}
        {tenantFeatures.product_features?.management_insights && (
          <MSInsightsSection
            dateParams={dateParams}
            branchIds={filterBranch !== 'all' ? filterBranch : undefined}
          />
        )}

        {/* Role-based content */}
        {user?.role === 'branch_manager' && <BranchManagerView data={data} branches={branches} tenantFeatures={tenantFeatures} />}
        {user?.role === 'regional_manager' && <RegionalManagerView data={data} tenantFeatures={tenantFeatures} />}
        {(user?.role === 'tenant_admin' || user?.role === 'platform_admin') && <CorporateView data={data} hasCases={hasCases} hasGapLibrary={hasGapLibrary} tenantFeatures={tenantFeatures} />}
      </div>
    </DashboardLayout>
  );
}

/* =============== NPS Breakdown KPI Card =============== */
function NpsBreakdownKpiCard({ breakdown, total, onSegmentClick }) {
  const promoters = Number(breakdown?.promoters || 0);
  const passives = Number(breakdown?.passives || 0);
  const detractors = Number(breakdown?.detractors || 0);
  const sum = promoters + passives + detractors;
  const pPct = sum > 0 ? Math.round((promoters / sum) * 100) : 0;
  const psPct = sum > 0 ? Math.round((passives / sum) * 100) : 0;
  const dPct = sum > 0 ? Math.round((detractors / sum) * 100) : 0;

  const Segment = ({ count, pct, color, category, label }) => {
    if (count === 0) return null;
    return (
      <button
        type="button"
        onClick={() => onSegmentClick && onSegmentClick(category)}
        className="h-full flex items-center justify-center transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-[#1A3E6F]"
        style={{ width: `${pct}%`, backgroundColor: color }}
        data-testid={`nps-breakdown-segment-${category}`}
        aria-label={`${label} ${pct}% (${count})`}
        title={`${label}: ${count} (${pct}%)`}
      >
        {pct >= 10 && <span className="text-xs font-semibold text-white">{pct}%</span>}
      </button>
    );
  };

  return (
    <Card className="border-[#E2E8F0] shadow-sm h-full" data-testid="nps-breakdown-kpi-card">
      <CardContent className="p-4 sm:p-5 h-full flex flex-col justify-center">
        <p className="text-xs sm:text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-3">Net Promoter® Score Breakdown</p>
        {sum === 0 ? (
          <div className="text-xs text-[#94A3B8] py-4 text-center">No NPS® responses yet</div>
        ) : (
          <>
            <div
              className="flex w-full rounded-md overflow-hidden bg-[#F1F5F9]"
              style={{ height: '36px' }}
              data-testid="nps-breakdown-bar-dashboard"
            >
              <Segment count={promoters} pct={pPct} color="#4EBC87" category="promoter" label="Promoters" />
              <Segment count={passives} pct={psPct} color="#F59E0B" category="passive" label="Passives" />
              <Segment count={detractors} pct={dPct} color="#D62B27" category="detractor" label="Detractors" />
            </div>
            <div className="flex justify-between mt-3 text-[11px] sm:text-xs text-[#64748B] gap-2 flex-wrap">
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#4EBC87]" />
                <span className="font-medium text-[#0F172A]">Promoters</span>
                <span>{pPct}% ({promoters})</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#F59E0B]" />
                <span className="font-medium text-[#0F172A]">Passives</span>
                <span>{psPct}% ({passives})</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#D62B27]" />
                <span className="font-medium text-[#0F172A]">Detractors</span>
                <span>{dPct}% ({detractors})</span>
              </span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/* =============== KPI Card =============== */
function KPICard({ icon: Icon, iconBg, iconColor, value, label, testId, trend, onClick, sublabel }) {
  const clickable = typeof onClick === 'function';
  return (
    <Card
      className={`border-[#E2E8F0] shadow-sm transition-all h-full ${clickable ? 'hover:shadow-md hover:-translate-y-0.5 cursor-pointer' : 'hover:shadow-md'}`}
      data-testid={testId}
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined}
    >
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between mb-3">
          <div className={`w-10 h-10 ${iconBg} rounded-lg flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${iconColor}`} />
          </div>
          {trend === 'up' && <TrendingUp className="w-4 h-4 text-[#4EBC87]" />}
          {trend === 'down' && <TrendingDown className="w-4 h-4 text-[#D62B27]" />}
        </div>
        <p className="text-2xl sm:text-3xl font-bold text-[#0F172A] font-['Montserrat']">{value}</p>
        <p className="text-xs sm:text-sm text-[#64748B] mt-0.5">{label}</p>
        {sublabel && <p className="text-[11px] text-[#1A3E6F] mt-0.5 font-medium">{sublabel}</p>}
      </CardContent>
    </Card>
  );
}

/* =============== Mystery Shopping Summary Card (Fix 2) ============== */
function MysteryShoppingSummaryCard({ published30d = 0, publishedTotal = 0, onView }) {
  return (
    <Card className="border-[#E2E8F0] shadow-sm bg-gradient-to-r from-[#F8FAFC] to-white" data-testid="mystery-shopping-summary">
      <CardContent className="p-5 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-[#1A3E6F]/10 flex items-center justify-center">
            <CheckCircle className="w-6 h-6 text-[#1A3E6F]" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">Mystery Shopping</p>
            <p className="font-['Montserrat'] text-xl font-bold text-[#0F172A]" data-testid="mystery-shopping-30d">
              {published30d} published report{published30d === 1 ? '' : 's'} <span className="text-sm text-[#64748B] font-normal">in last 30 days</span>
            </p>
            {publishedTotal > 0 && (
              <p className="text-xs text-[#64748B] mt-0.5" data-testid="mystery-shopping-total">
                {publishedTotal} total published
              </p>
            )}
          </div>
        </div>
        <Button onClick={onView} variant="outline" data-testid="mystery-shopping-view-btn">
          View Reports <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </CardContent>
    </Card>
  );
}

/* =============== BRANCH MANAGER VIEW =============== */
function BranchManagerView({ data, tenantFeatures = {} }) {
  const { response_stats, case_status, top_gaps, pending_actions, repeat_issues, nps_trend } = data;
  const sentimentData = [
    { name: 'Positive', value: response_stats.sentiment_split.positive },
    { name: 'Neutral', value: response_stats.sentiment_split.neutral },
    { name: 'Negative', value: response_stats.sentiment_split.negative },
  ].filter(d => d.value > 0);

  return (
    <>
      {/* Row 1: Sentiment + NPS Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-[#E2E8F0] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-['Montserrat'] text-[#0F172A]">Sentiment Split</CardTitle>
          </CardHeader>
          <CardContent data-testid="sentiment-chart">
            {sentimentData.length > 0 ? (
              <div className="flex items-center gap-6">
                <ResponsiveContainer width={160} height={160}>
                  <PieChart>
                    <Pie data={sentimentData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" stroke="none">
                      {sentimentData.map((_, i) => <Cell key={i} fill={SENTIMENT_COLORS[i]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => [v, 'Responses']} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2">
                  {[['Positive', COLORS.green], ['Neutral', COLORS.yellow], ['Negative', COLORS.red]].map(([label, color]) => (
                    <div key={label} className="flex items-center gap-2 text-sm">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                      <span className="text-[#64748B]">{label}:</span>
                      <span className="font-semibold text-[#0F172A]">{response_stats.sentiment_split[label.toLowerCase()]}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : <EmptyState message="No responses yet" />}
          </CardContent>
        </Card>

        <Card className="border-[#E2E8F0] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-['Montserrat'] text-[#0F172A]">Net Promoter® Score Trend</CardTitle>
          </CardHeader>
          <CardContent data-testid="nps-trend-chart">
            {nps_trend?.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={nps_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)} />
                  <YAxis domain={[-100, 100]} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => [v, 'NPS® index']} />
                  <ReferenceLine y={0} stroke="#94A3B8" strokeDasharray="3 3" />
                  <Line type="monotone" dataKey="nps_index" stroke={COLORS.blue} strokeWidth={2} dot={{ fill: COLORS.blue, r: 4 }} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            ) : <EmptyState message="Not enough data for trend" />}
          </CardContent>
        </Card>
      </div>

      {/* Row 2: Open Cases + Repeat Issues — Fix 2: hidden behind
          tenant.features.cases_widgets (default OFF). The cards still
          live in the codebase so they can be re-enabled per-tenant. */}
      {tenantFeatures.cases_widgets && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-[#E2E8F0] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-['Montserrat'] text-[#0F172A] flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-[#D62B27]" /> Open Cases
            </CardTitle>
          </CardHeader>
          <CardContent data-testid="open-cases-section">
            <CaseStatusBars status={case_status} />
            {top_gaps?.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-medium text-[#64748B] mb-2 uppercase tracking-wide">Top Issues</p>
                <div className="space-y-2">
                  {top_gaps.slice(0, 5).map((g, i) => (
                    <div key={g.gap || i} className="flex items-center justify-between text-sm">
                      <span className="text-[#0F172A] truncate">{g.gap}</span>
                      <Badge variant="outline" className="ml-2 shrink-0">{g.count}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-[#E2E8F0] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-['Montserrat'] text-[#0F172A] flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-[#F97316]" /> Repeat Issues
            </CardTitle>
          </CardHeader>
          <CardContent data-testid="repeat-issues-section">
            {repeat_issues?.length > 0 ? (
              <div className="space-y-3">
                {repeat_issues.slice(0, 5).map((r, i) => (
                  <div key={`${r.gap}-${r.branch_id}`} className="flex items-center justify-between p-2.5 bg-[#FFF7ED] rounded-lg border border-[#FFEDD5]">
                    <div>
                      <p className="text-sm font-medium text-[#0F172A]">{r.gap}</p>
                      <p className="text-xs text-[#64748B]">{r.branch_name}</p>
                    </div>
                    <Badge className="bg-[#F97316]/15 text-[#F97316]">{r.occurrences}x</Badge>
                  </div>
                ))}
              </div>
            ) : <EmptyState message="No repeat issues detected" />}
          </CardContent>
        </Card>
      </div>
      )}

      {/* Row 3: Pending Actions — Fix 2: same feature flag */}
      {tenantFeatures.cases_widgets && (
        <PendingActionsCard actions={pending_actions} />
      )}
    </>
  );
}

/* =============== REGIONAL MANAGER VIEW =============== */
function RegionalManagerView({ data, tenantFeatures = {} }) {
  const { branch_comparison, issue_trends, case_status, action_completion, nps_trend, repeat_issues } = data;

  const branchChartData = branch_comparison?.slice(0, 8).map(b => ({
    name: b.branch_name?.length > 12 ? b.branch_name.slice(0, 12) + '..' : b.branch_name,
    nps_index: b.nps_index,
    responses: b.responses,
  })) || [];

  return (
    <>
      {/* Branch Comparison */}
      <Card className="border-[#E2E8F0] shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-['Montserrat'] text-[#0F172A] flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[#1A3E6F]" /> Branch Comparison
          </CardTitle>
        </CardHeader>
        <CardContent data-testid="branch-comparison">
          {branchChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={branchChartData} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis domain={[-100, 100]} tick={{ fontSize: 11 }} />
                <ReferenceLine y={0} stroke="#94A3B8" strokeDasharray="3 3" />
                <Tooltip />
                <Legend />
                <Bar dataKey="nps_index" fill={COLORS.blue} name="Net Promoter® Score" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyState message="No branch data available" />}
          {/* Branch table */}
          {branch_comparison?.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm" data-testid="branch-table">
                <thead>
                  <tr className="border-b border-[#E2E8F0]">
                    <th className="text-left py-2 pr-4 text-xs font-semibold text-[#64748B] uppercase">Branch</th>
                    <th className="text-center py-2 px-2 text-xs font-semibold text-[#64748B] uppercase">Responses</th>
                    <th className="text-center py-2 px-2 text-xs font-semibold text-[#64748B] uppercase">Net Promoter® Score</th>
                    <th className="text-center py-2 px-2 text-xs font-semibold text-[#64748B] uppercase">P / Pa / D</th>
                    <th className="text-center py-2 px-2 text-xs font-semibold text-[#64748B] uppercase">Avg Sat.</th>
                    <th className="text-center py-2 px-2 text-xs font-semibold text-[#64748B] uppercase">Open Cases</th>
                    <th className="text-center py-2 pl-2 text-xs font-semibold text-[#64748B] uppercase">Resolution</th>
                  </tr>
                </thead>
                <tbody>
                  {branch_comparison.map((b, i) => (
                    <tr key={b.branch_id || i} className="border-b border-[#F1F5F9]">
                      <td className="py-2.5 pr-4 font-medium text-[#0F172A]">{b.branch_name}</td>
                      <td className="py-2.5 px-2 text-center">{b.responses}</td>
                      <td className="py-2.5 px-2 text-center">
                        <span className={`font-semibold ${npsColorClass(b.nps_index)}`}>
                          {formatNpsIndex(b.nps_index)}
                        </span>
                      </td>
                      <td className="py-2.5 px-2 text-center text-xs">
                        <span className="text-[#4EBC87]">{b.promoters ?? 0}</span>
                        <span className="text-[#94A3B8]"> / </span>
                        <span className="text-[#475569]">{b.passives ?? 0}</span>
                        <span className="text-[#94A3B8]"> / </span>
                        <span className="text-[#D62B27]">{b.detractors ?? 0}</span>
                      </td>
                      <td className="py-2.5 px-2 text-center">{b.avg_satisfaction ? `${Number(b.avg_satisfaction).toFixed(1)} / 5` : '–'}</td>
                      <td className="py-2.5 px-2 text-center">{b.open_cases}</td>
                      <td className="py-2.5 pl-2 text-center">{b.resolution_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Row 2: Issue Trends + Case Resolution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-[#E2E8F0] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-['Montserrat'] text-[#0F172A] flex items-center gap-2">
              <Activity className="w-4 h-4 text-[#603795]" /> Issue Trends
            </CardTitle>
          </CardHeader>
          <CardContent data-testid="issue-trends">
            {issue_trends?.length > 0 ? (
              <div className="space-y-2.5">
                {issue_trends.slice(0, 8).map((t, i) => (
                  <div key={`${t.department}-${t.category}`} className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-[#0F172A] truncate">{t.category || t.department}</span>
                        <span className="text-xs text-[#64748B] ml-2">{t.count} cases ({t.open} open)</span>
                      </div>
                      <Progress value={(t.count / (issue_trends[0]?.count || 1)) * 100} className="h-2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : <EmptyState message="No issue trends" />}
          </CardContent>
        </Card>

        <Card className="border-[#E2E8F0] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-['Montserrat'] text-[#0F172A] flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-[#4EBC87]" /> Action Completion
            </CardTitle>
          </CardHeader>
          <CardContent data-testid="action-completion">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-[#F8F9FA] rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-[#0F172A] font-['Montserrat']">{action_completion.action_rate}%</p>
                <p className="text-xs text-[#64748B]">Actions Taken</p>
              </div>
              <div className="bg-[#F8F9FA] rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-[#0F172A] font-['Montserrat']">{action_completion.closure_rate}%</p>
                <p className="text-xs text-[#64748B]">Cases Closed</p>
              </div>
            </div>
            <CaseStatusBars status={case_status} />
          </CardContent>
        </Card>
      </div>

      {/* Unresolved Cases — gated on tenant cases_widgets feature flag */}
      {tenantFeatures.cases_widgets && (
        <PendingActionsCard actions={data.pending_actions} title="Unresolved Cases" />
      )}
    </>
  );
}

/* =============== CORPORATE (TENANT ADMIN) VIEW =============== */
function CorporateView({ data, hasCases, hasGapLibrary, tenantFeatures = {} }) {
  const navigate = useNavigate();
  const { response_stats, branch_comparison, top_gaps, issue_trends, action_completion,
    nps_trend, repeat_issues, case_status, satisfaction_distribution, satisfaction_question, tagged_gaps, top_dropdown } = data;

  const sentimentData = [
    { name: 'Positive', value: response_stats.sentiment_split.positive },
    { name: 'Neutral', value: response_stats.sentiment_split.neutral },
    { name: 'Negative', value: response_stats.sentiment_split.negative },
  ].filter(d => d.value > 0);

  const branchRanking = [...(branch_comparison || [])].sort(
    (a, b) => (b.nps_index ?? -101) - (a.nps_index ?? -101)
  );
  const topGapsChart = top_gaps?.slice(0, 6).map(g => ({
    name: g.gap?.length > 18 ? g.gap.slice(0, 18) + '..' : g.gap,
    count: g.count,
  })) || [];

  return (
    <>
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="bg-[#F1F5F9]" data-testid="corporate-tabs">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="branches">Branch Ranking</TabsTrigger>
          {hasGapLibrary && <TabsTrigger value="issues">Issues & Gaps</TabsTrigger>}
        </TabsList>

        {/* OVERVIEW TAB */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sentiment donut — clickable, renamed, with explanatory subtitle. */}
            <Card className="border-[#E2E8F0] shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-['Montserrat'] text-[#0F172A]">Response Text Sentiment</CardTitle>
                <p className="text-xs text-[#64748B] mt-0.5">Based on AI analysis of open-ended answers.</p>
              </CardHeader>
              <CardContent data-testid="corporate-sentiment">
                {sentimentData.length > 0 ? (
                  <div className="flex items-center gap-6">
                    <ResponsiveContainer width={160} height={160}>
                      <PieChart>
                        <Pie
                          data={sentimentData}
                          cx="50%" cy="50%"
                          innerRadius={45} outerRadius={70}
                          dataKey="value" stroke="none"
                          onClick={(_, idx) => {
                            const labelMap = ['positive', 'neutral', 'negative'];
                            navigate(`/responses?sentiment=${labelMap[idx]}`);
                          }}
                          cursor="pointer"
                        >
                          {sentimentData.map((_, i) => <Cell key={i} fill={SENTIMENT_COLORS[i]} />)}
                        </Pie>
                        <Tooltip formatter={(v) => [v, 'Responses']} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-3">
                      {[['Positive', COLORS.green, 'positive'], ['Neutral', COLORS.yellow, 'neutral'], ['Negative', COLORS.red, 'negative']].map(([label, color, key]) => {
                        const val = response_stats.sentiment_split[key];
                        const pct = response_stats.total_responses > 0 ? Math.round(val / response_stats.total_responses * 100) : 0;
                        return (
                          <button
                            type="button"
                            key={label}
                            onClick={() => navigate(`/responses?sentiment=${key}`)}
                            className="flex items-center gap-2 text-sm hover:underline cursor-pointer"
                            data-testid={`sentiment-${key}-link`}
                          >
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                            <span className="text-[#64748B]">{label}:</span>
                            <span className="font-semibold text-[#0F172A]">{val}</span>
                            <span className="text-[#64748B]">({pct}%)</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : <EmptyState message="No responses yet" />}
              </CardContent>
            </Card>

            {/* Daily NPS index (-100..+100) — replaces avg score plot. */}
            <Card className="border-[#E2E8F0] shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-['Montserrat'] text-[#0F172A]">Net Promoter® Score Trend</CardTitle>
                <p className="text-xs text-[#64748B] mt-0.5">Daily NPS&reg; index. Days with fewer than 3 responses are omitted.</p>
              </CardHeader>
              <CardContent data-testid="corporate-nps-trend">
                {nps_trend?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={nps_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)} />
                      <YAxis domain={[-100, 100]} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => [v, 'NPS® index']} />
                      <ReferenceLine y={0} stroke="#94A3B8" strokeDasharray="3 3" />
                      <Line
                        type="monotone"
                        dataKey="nps_index"
                        stroke={COLORS.blue}
                        strokeWidth={2}
                        dot={{ fill: COLORS.blue, r: 4 }}
                        name="NPS® index"
                        connectNulls
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <EmptyState message="Not enough data for trend" />}
              </CardContent>
            </Card>
          </div>

          {/* Satisfaction distribution + Top dropdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SatisfactionDistributionCard data={satisfaction_distribution} question={satisfaction_question} />
            {top_dropdown && <TopDropdownCard data={top_dropdown} />}
          </div>

          {/* Tagged gaps */}
          {Array.isArray(tagged_gaps) && tagged_gaps.length > 0 && (
            <TaggedGapsCard data={tagged_gaps} />
          )}

          {/* Action Completion + Case Status — gated on tenant cases_widgets flag (Fix 2) */}
          {hasCases && tenantFeatures.cases_widgets && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card className="border-[#E2E8F0] shadow-sm">
                <CardContent className="p-4 text-center" data-testid="metric-action-rate">
                  <p className="text-3xl font-bold text-[#1A3E6F] font-['Montserrat']">{action_completion.action_rate}%</p>
                  <p className="text-xs text-[#64748B] mt-1">Action Rate</p>
                  <Progress value={action_completion.action_rate} className="h-2 mt-2" />
                </CardContent>
              </Card>
              <Card className="border-[#E2E8F0] shadow-sm">
                <CardContent className="p-4 text-center" data-testid="metric-closure-rate">
                  <p className="text-3xl font-bold text-[#4EBC87] font-['Montserrat']">{action_completion.closure_rate}%</p>
                  <p className="text-xs text-[#64748B] mt-1">Closure Rate</p>
                  <Progress value={action_completion.closure_rate} className="h-2 mt-2" />
                </CardContent>
              </Card>
              <Card className="border-[#E2E8F0] shadow-sm">
                <CardContent className="p-4 text-center" data-testid="metric-total-cases">
                  <p className="text-3xl font-bold text-[#D62B27] font-['Montserrat']">{case_status.open + case_status.in_progress}</p>
                  <p className="text-xs text-[#64748B] mt-1">Open Cases</p>
                  <CaseStatusBars status={case_status} compact />
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* BRANCH RANKING TAB */}
        <TabsContent value="branches" className="space-y-6">
          <Card className="border-[#E2E8F0] shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-['Montserrat'] text-[#0F172A]">Branch Ranking by Net Promoter® Score</CardTitle>
            </CardHeader>
            <CardContent data-testid="branch-ranking">
              {branchRanking.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#E2E8F0]">
                        <th className="text-left py-2 pr-3 text-xs font-semibold text-[#64748B] uppercase w-8">#</th>
                        <th className="text-left py-2 pr-4 text-xs font-semibold text-[#64748B] uppercase">Branch</th>
                        <th className="text-center py-2 px-3 text-xs font-semibold text-[#64748B] uppercase">Responses</th>
                        <th className="text-center py-2 px-3 text-xs font-semibold text-[#64748B] uppercase">Net Promoter® Score</th>
                        <th className="text-center py-2 px-3 text-xs font-semibold text-[#64748B] uppercase">Promoters</th>
                        <th className="text-center py-2 px-3 text-xs font-semibold text-[#64748B] uppercase">Passives</th>
                        <th className="text-center py-2 px-3 text-xs font-semibold text-[#64748B] uppercase">Detractors</th>
                        <th className="text-center py-2 px-3 text-xs font-semibold text-[#64748B] uppercase">Avg Sat.</th>
                        <th className="text-center py-2 px-3 text-xs font-semibold text-[#64748B] uppercase">Cases</th>
                        <th className="text-center py-2 px-3 text-xs font-semibold text-[#64748B] uppercase">Open</th>
                        <th className="text-center py-2 pl-3 text-xs font-semibold text-[#64748B] uppercase">Resolution</th>
                      </tr>
                    </thead>
                    <tbody>
                      {branchRanking.map((b, i) => (
                        <tr key={b.branch_id || i} className="border-b border-[#F1F5F9] hover:bg-[#F8F9FA]">
                          <td className="py-3 pr-3">
                            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold
                              ${i === 0 ? 'bg-[#F1D511]/20 text-[#b45309]' : i === 1 ? 'bg-[#E2E8F0] text-[#64748B]' : i === 2 ? 'bg-[#F97316]/15 text-[#F97316]' : 'text-[#64748B]'}`}>
                              {i + 1}
                            </span>
                          </td>
                          <td className="py-3 pr-4 font-medium text-[#0F172A]">{b.branch_name}</td>
                          <td className="py-3 px-3 text-center">{b.responses}</td>
                          <td className="py-3 px-3 text-center">
                            <span className={`font-semibold ${npsColorClass(b.nps_index)}`}>
                              {formatNpsIndex(b.nps_index)}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-center text-[#4EBC87]">{Number(b.promoters_pct ?? 0).toFixed(0)}% <span className="text-[#94A3B8]">({b.promoters})</span></td>
                          <td className="py-3 px-3 text-center text-[#475569]">{Number(b.passives_pct ?? 0).toFixed(0)}% <span className="text-[#94A3B8]">({b.passives})</span></td>
                          <td className="py-3 px-3 text-center text-[#D62B27]">{Number(b.detractors_pct ?? 0).toFixed(0)}% <span className="text-[#94A3B8]">({b.detractors})</span></td>
                          <td className="py-3 px-3 text-center">{b.avg_satisfaction ? `${Number(b.avg_satisfaction).toFixed(1)} / 5` : '–'}</td>
                          <td className="py-3 px-3 text-center">{b.total_cases}</td>
                          <td className="py-3 px-3 text-center">
                            {b.open_cases > 0 ? <Badge className="bg-[#D62B27]/15 text-[#D62B27]">{b.open_cases}</Badge> : <span className="text-[#4EBC87]">0</span>}
                          </td>
                          <td className="py-3 pl-3 text-center">{b.resolution_rate}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <EmptyState message="No branch data" />}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ISSUES TAB */}
        {hasGapLibrary && (
        <TabsContent value="issues" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-[#E2E8F0] shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-['Montserrat'] text-[#0F172A]">Top Gaps</CardTitle>
              </CardHeader>
              <CardContent data-testid="top-gaps-chart">
                {topGapsChart.length > 0 ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={topGapsChart} layout="vertical" margin={{ left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={130} />
                      <Tooltip />
                      <Bar dataKey="count" fill={COLORS.red} radius={[0, 4, 4, 0]} name="Cases" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <EmptyState message="No gap data" />}
              </CardContent>
            </Card>

            <Card className="border-[#E2E8F0] shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-['Montserrat'] text-[#0F172A] flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-[#F97316]" /> Recurring Issues
                </CardTitle>
              </CardHeader>
              <CardContent data-testid="recurring-issues">
                {repeat_issues?.length > 0 ? (
                  <div className="space-y-3">
                    {repeat_issues.map((r, i) => (
                      <div key={`${r.gap}-${r.branch_id}`} className="flex items-center justify-between p-3 bg-[#FFF7ED] rounded-lg border border-[#FFEDD5]">
                        <div>
                          <p className="text-sm font-medium text-[#0F172A]">{r.gap}</p>
                          <p className="text-xs text-[#64748B]">{r.branch_name}</p>
                        </div>
                        <Badge className="bg-[#F97316]/15 text-[#F97316]">{r.occurrences}x</Badge>
                      </div>
                    ))}
                  </div>
                ) : <EmptyState message="No recurring issues" />}
              </CardContent>
            </Card>
          </div>

          {/* Issue Trends by Department */}
          <Card className="border-[#E2E8F0] shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-['Montserrat'] text-[#0F172A]">Issue Trends by Category</CardTitle>
            </CardHeader>
            <CardContent data-testid="issue-trends-corporate">
              {issue_trends?.length > 0 ? (
                <div className="space-y-2.5">
                  {issue_trends.map((t, i) => (
                    <div key={`${t.department}-${t.category}`} className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-sm text-[#0F172A] truncate">{t.category || 'Unknown'}</span>
                            <span className="text-xs text-[#64748B] shrink-0">({t.department})</span>
                          </div>
                          <span className="text-xs text-[#64748B] ml-2 shrink-0">{t.count} total, {t.open} open</span>
                        </div>
                        <Progress value={(t.count / (issue_trends[0]?.count || 1)) * 100} className="h-2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : <EmptyState message="No issue trends" />}
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>
    </>
  );
}

/* =============== SHARED COMPONENTS =============== */

function CaseStatusBars({ status, compact }) {
  const total = (status?.open || 0) + (status?.in_progress || 0) + (status?.closed || 0);
  if (total === 0) return compact ? null : <EmptyState message="No cases" />;
  const pct = (v) => Math.round((v / total) * 100);
  return (
    <div className={compact ? 'mt-2' : ''}>
      <div className="flex rounded-full overflow-hidden h-3 bg-[#F1F5F9]">
        {status.open > 0 && <div style={{ width: `${pct(status.open)}%` }} className="bg-[#D62B27]" />}
        {status.in_progress > 0 && <div style={{ width: `${pct(status.in_progress)}%` }} className="bg-[#F1D511]" />}
        {status.closed > 0 && <div style={{ width: `${pct(status.closed)}%` }} className="bg-[#4EBC87]" />}
      </div>
      {!compact && (
        <div className="flex justify-between mt-2 text-xs text-[#64748B]">
          <span>Open: {status.open}</span>
          <span>In Progress: {status.in_progress}</span>
          <span>Closed: {status.closed}</span>
        </div>
      )}
    </div>
  );
}

function PendingActionsCard({ actions, title = 'Pending Actions' }) {
  const priorityColors = { critical: 'bg-[#D62B27]/15 text-[#D62B27]', high: 'bg-[#F97316]/15 text-[#F97316]', medium: 'bg-[#5BC4BF]/15 text-[#5BC4BF]', low: 'bg-[#64748B]/15 text-[#64748B]' };
  return (
    <Card className="border-[#E2E8F0] shadow-sm">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-['Montserrat'] text-[#0F172A] flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#F97316]" /> {title}
          </CardTitle>
          {actions?.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => window.location.href = '/cases'} className="text-[#1A3E6F] text-xs" data-testid="view-all-cases-btn">
              View All <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent data-testid="pending-actions">
        {actions?.length > 0 ? (
          <div className="space-y-2">
            {actions.slice(0, 8).map((a, i) => (
              <div key={a.case_id || i} className="flex items-center justify-between p-3 rounded-lg border border-[#E2E8F0] hover:bg-[#F8F9FA] transition-colors">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-medium text-[#0F172A] truncate">{a.gap}</span>
                    <Badge className={priorityColors[a.priority] || ''}>{a.priority}</Badge>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[#64748B]">
                    <span>{a.branch_name}</span>
                    {a.suggested_action && <span className="truncate max-w-[200px]">Suggestion: {a.suggested_action}</span>}
                  </div>
                </div>
                <Badge variant="outline" className={a.status === 'open' ? 'border-[#D62B27]/30 text-[#D62B27]' : 'border-[#F1D511]/30 text-[#b45309]'}>
                  {a.status === 'open' ? 'Open' : 'In Progress'}
                </Badge>
              </div>
            ))}
          </div>
        ) : <EmptyState message="All cases have actions taken" />}
      </CardContent>
    </Card>
  );
}

function EmptyState({ message }) {
  return (
    <div className="py-6 text-center text-sm text-[#64748B]">
      <BarChart3 className="w-8 h-8 mx-auto mb-2 text-[#E2E8F0]" />
      {message}
    </div>
  );
}

/* =============== Satisfaction distribution (1–5 score counts) =============== */
function SatisfactionDistributionCard({ data, question }) {
  const rows = (data || []).filter(d => d && d.count > 0);
  // B-Fix3: title comes from the auto-detected rating question when present.
  // Strips HTML tags + entities and truncates to 40 chars + ellipsis.
  let title = 'Satisfaction Score Distribution';
  let fullText = title;
  if (question && question.question_text) {
    const stripped = cleanQuestionLabel(question.question_text);
    if (stripped) {
      fullText = stripped;
      title = stripped.length > 40 ? stripped.slice(0, 40).trim() + '…' : stripped;
    }
  }
  if (!rows.length && !question) return null; // hide entirely when no rating data
  return (
    <Card className="border-[#E2E8F0] shadow-sm" data-testid="card-satisfaction-distribution">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-['Montserrat'] text-[#0F172A] truncate" title={fullText}>{title}</CardTitle>
        <p className="text-xs text-[#64748B] mt-0.5">Responses grouped by satisfaction score.</p>
      </CardHeader>
      <CardContent>
        {rows.length > 0 ? (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={rows} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="score" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => [v, 'Responses']} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {rows.map((r, i) => <Cell key={i} fill={satScoreColor(r.score)} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : <EmptyState message="No satisfaction scores in this period" />}
      </CardContent>
    </Card>
  );
}

/* =============== Top dropdown / single-choice question =============== */
function TopDropdownCard({ data }) {
  if (!data || !Array.isArray(data.items) || data.items.length === 0) return null;
  // B-Fix4: use the shared platform-wide question-label helper to strip
  // any legacy HTML/entities, then truncate at the first '?' or ',' inside
  // 60 chars, otherwise hard-cap at 40 chars. Tooltip shows the full text.
  const stripped = cleanQuestionLabel(data.question_text);
  const truncate = (s) => {
    if (!s) return '';
    if (s.length <= 40) return s;
    const stop = s.slice(0, 60).search(/[?,]/);
    if (stop > 0 && stop < 60) return s.slice(0, stop + 1).trim();
    return s.slice(0, 40).trim() + '…';
  };
  const title = truncate(stripped);
  return (
    <Card className="border-[#E2E8F0] shadow-sm" data-testid="card-top-dropdown">
      <CardHeader className="pb-2">
        <CardTitle
          className="text-base font-['Montserrat'] text-[#0F172A] truncate"
          title={stripped}
        >
          {title || 'Top Answers'}
        </CardTitle>
        <p className="text-xs text-[#64748B] mt-0.5">Most frequent answers to this {data.type === 'dropdown' ? 'dropdown' : 'choice'} question.</p>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={Math.max(160, data.items.length * 26)}>
          <BarChart data={data.items} layout="vertical" margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
            <YAxis type="category" dataKey="label" width={140} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => [v, 'Responses']} />
            <Bar dataKey="count" fill={COLORS.blue} radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

/* =============== Tagged Gaps (areas of concern) =============== */
function TaggedGapsCard({ data }) {
  if (!Array.isArray(data) || data.length === 0) return null;
  // Top 2 red, next 2 orange, remainder grey.
  const colored = data.map((g, i) => ({
    ...g,
    fill: i < 2 ? COLORS.red : i < 4 ? COLORS.orange : COLORS.grey,
  }));
  return (
    <Card className="border-[#E2E8F0] shadow-sm" data-testid="card-tagged-gaps">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-['Montserrat'] text-[#0F172A]">Tagged Gaps (Areas of Concern)</CardTitle>
        <p className="text-xs text-[#64748B] mt-0.5">Frequency of gaps tagged on responses in the current filter.</p>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={Math.max(180, colored.length * 28)}>
          <BarChart data={colored} layout="vertical" margin={{ left: 8, right: 32, top: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
            <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => [v, 'Tagged']} />
            <Bar dataKey="count" radius={[0, 4, 4, 0]} label={{ position: 'right', fontSize: 11, fill: '#475569' }}>
              {colored.map((g, i) => <Cell key={i} fill={g.fill} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}



/* =============== Management Insights (Client Experience V2) =============== */
function ResponseChip({ publicId }) {
  return (
    <a
      href={`/reports/${publicId}`}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center px-2 py-0.5 mr-1 mb-1 rounded-md text-[11px] font-mono
                 bg-[#F1F5F9] text-[#1A3E6F] border border-[#E2E8F0]
                 hover:bg-[#E2E8F0] hover:underline"
      data-testid={`insight-response-${publicId}`}
    >
      {publicId}
    </a>
  );
}

function InsightRow({ item, testidPrefix }) {
  return (
    <div className="py-2.5 border-b border-[#F1F5F9] last:border-b-0"
         data-testid={`${testidPrefix}-${item.count}`}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-[#0F172A] leading-relaxed">{item.phrase}</p>
        <span className="text-[11px] font-medium text-[#64748B] whitespace-nowrap">
          {item.count} FIND{item.count > 1 ? 's' : ''}
        </span>
      </div>
      {item.branches?.length > 0 && (
        <p className="text-[11px] text-[#94A3B8] mt-0.5">
          {item.branches.slice(0, 3).join(' · ')}
          {item.branches.length > 3 && ` +${item.branches.length - 3} more`}
        </p>
      )}
      <div className="mt-1.5 flex flex-wrap">
        {item.response_ids.slice(0, 8).map((pid) => (
          <ResponseChip key={pid} publicId={pid} />
        ))}
        {item.response_ids.length > 8 && (
          <span className="text-[11px] text-[#64748B] ml-1 self-center">
            +{item.response_ids.length - 8}
          </span>
        )}
      </div>
    </div>
  );
}

function BranchRow({ row, tone }) {
  const chipClass =
    tone === 'attention'
      ? 'bg-rose-50 text-rose-700 border-rose-200'
      : tone === 'excellence'
        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
        : 'bg-slate-50 text-slate-700 border-slate-200';
  return (
    <div className="py-2.5 border-b border-[#F1F5F9] last:border-b-0"
         data-testid={`insight-branch-${row.branch_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-[#0F172A] truncate">{row.branch_name}</p>
          <p className="text-[11px] text-[#94A3B8]">
            {row.n_finds} published FIND{row.n_finds > 1 ? 's' : ''}
          </p>
        </div>
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${chipClass} whitespace-nowrap`}>
          TrustX® {row.trustx_mean}
        </span>
      </div>
      <div className="mt-1.5 flex flex-wrap">
        {row.response_ids.slice(0, 6).map((pid) => <ResponseChip key={pid} publicId={pid} />)}
        {row.response_ids.length > 6 && (
          <span className="text-[11px] text-[#64748B] ml-1 self-center">
            +{row.response_ids.length - 6}
          </span>
        )}
      </div>
    </div>
  );
}

function EmptyBlock({ label }) {
  return (
    <p className="text-xs text-[#94A3B8] py-6 text-center italic">
      Not enough published FINDs yet to surface {label}.
    </p>
  );
}

export function ManagementInsightsCard({ dateParams, branchIds }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const q = new URLSearchParams();
        if (dateParams?.from) q.append('date_from', dateParams.from);
        if (dateParams?.to) q.append('date_to', dateParams.to);
        if (branchIds) q.append('branch_ids', branchIds);
        const r = await fetch(`${API}/reports/insights?${q}`, { credentials: 'include' });
        if (r.ok && alive) setData(await r.json());
        else if (alive) setData(null);
      } catch (e) {
        console.error('insights fetch failed:', e);
        if (alive) setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [dateParams?.from, dateParams?.to, branchIds]);

  return (
    <Card className="border-[#E2E8F0] shadow-sm" data-testid="management-insights-card">
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
          <div>
            <CardTitle className="text-base font-['Montserrat'] flex items-center gap-2">
              <Activity className="w-4 h-4 text-[#1A3E6F]" />
              Management Insights
            </CardTitle>
            <p className="text-xs text-[#64748B] mt-1">
              Evidence from your published FINDs. Click any Response ID to open the underlying report.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                const q = new URLSearchParams();
                if (dateParams?.from) q.append('date_from', dateParams.from);
                if (dateParams?.to) q.append('date_to', dateParams.to);
                if (branchIds) q.append('branch_ids', branchIds);
                const r = await fetch(`${API}/reports/pdf/management-summary?${q.toString()}`, { credentials: 'include' });
                if (!r.ok) throw new Error('summary failed');
                const blob = await r.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'satisFIND_Management_Summary.pdf';
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              } catch (e) {
                toast.error('Could not generate the Management Summary PDF');
              }
            }}
            data-testid="management-insights-summary-pdf"
          >
            <Download className="w-4 h-4 mr-1.5" />
            Download Management Summary PDF
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-xs text-[#94A3B8] py-6 text-center">Loading…</p>
        ) : !data || data.totals.published_finds === 0 ? (
          <EmptyBlock label="insights" />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <section data-testid="insights-strengths">
              <h4 className="text-xs font-semibold text-[#4EBC87] uppercase tracking-wider mb-2">
                Recurring Strengths
              </h4>
              {data.recurring_strengths.length === 0
                ? <EmptyBlock label="recurring strengths" />
                : data.recurring_strengths.map((it, i) => (
                    <InsightRow key={i} item={it} testidPrefix="insight-strength" />
                  ))}
            </section>

            <section data-testid="insights-opportunities">
              <h4 className="text-xs font-semibold text-[#F97316] uppercase tracking-wider mb-2">
                Recurring Opportunities
              </h4>
              {data.recurring_opportunities.length === 0
                ? <EmptyBlock label="recurring opportunities" />
                : data.recurring_opportunities.map((it, i) => (
                    <InsightRow key={i} item={it} testidPrefix="insight-opportunity" />
                  ))}
            </section>

            <section data-testid="insights-attention">
              <h4 className="text-xs font-semibold text-[#D62B27] uppercase tracking-wider mb-2">
                Branches Needing Attention
              </h4>
              {data.branches_needing_attention.length === 0
                ? <EmptyBlock label="branches needing attention" />
                : data.branches_needing_attention.map((r) => (
                    <BranchRow key={r.branch_id} row={r} tone="attention" />
                  ))}
            </section>

            <section data-testid="insights-excellence">
              <h4 className="text-xs font-semibold text-[#4EBC87] uppercase tracking-wider mb-2">
                Consistently Strong Branches
              </h4>
              {data.consistently_strong_branches.length === 0
                ? <EmptyBlock label="consistently strong branches" />
                : data.consistently_strong_branches.map((r) => (
                    <BranchRow key={r.branch_id} row={r} tone="excellence" />
                  ))}
            </section>
          </div>
        )}

        {/* Consolidated Branch satisACTION® — one cumulative section
            per client branch showing every distinct strength /
            opportunity across all qualifying published FINDs. Sits
            BELOW the overall Management Insights grid and never
            duplicates it (business scope vs. per-branch history). */}
        {!loading && data?.consolidated_branch_satisaction?.length > 0 && (
          <section className="mt-6 pt-4 border-t border-[#E2E8F0] space-y-5"
                   data-testid="consolidated-branch-satisaction">
            <h3 className="text-sm font-semibold text-[#1A3E6F] font-['Montserrat']">
              Consolidated Branch satisACTION®
            </h3>
            <p className="text-xs text-[#64748B]">
              Everything identified at each branch across the selected scope. Click any FIND ID to open the underlying report.
            </p>
            {data.consolidated_branch_satisaction.map((b) => (
              <BranchSatisactionBlock key={b.branch_id} branch={b} />
            ))}
          </section>
        )}
      </CardContent>
    </Card>
  );
}


/* One branch section: header + subsections for strengths and
   opportunities. Empty subsections are hidden. Single-FIND branches
   collapse to plain "Strengths Identified" / "Opportunities Identified"
   (no "Recurring" language). */
function BranchSatisactionBlock({ branch }) {
  const n = branch.n_finds || 0;
  const single = n === 1;
  const strengths = single
    ? [...(branch.recurring_strengths || []), ...(branch.other_strengths || [])]
    : null;
  const opps = single
    ? [...(branch.recurring_opportunities || []), ...(branch.other_opportunities || [])]
    : null;

  return (
    <div className="rounded-lg border border-[#E2E8F0] p-4"
         data-testid={`branch-sat-${branch.branch_id}`}>
      <div className="flex items-baseline justify-between flex-wrap gap-1">
        <p className="text-sm font-semibold text-[#0F172A]">{branch.branch_name}</p>
        <p className="text-[11px] text-[#64748B]"
           data-testid={`branch-sat-${branch.branch_id}-meta`}>
          {single ? 'Based on 1 published FIND' :
            `${n} FINDs analysed`}
          {formatBranchPeriod(branch.period_start, branch.period_end)}
        </p>
      </div>

      {single ? (
        <>
          {strengths.length === 0 && opps.length === 0 && (
            <p className="text-xs text-[#94A3B8] italic mt-2">
              No satisACTION® strengths or opportunities recorded on this FIND.
            </p>
          )}
          {strengths.length > 0 && (
            <BranchSubsection title="Strengths Identified" tone="strength"
                              items={strengths} totalFinds={n} />
          )}
          {opps.length > 0 && (
            <BranchSubsection title="Opportunities Identified" tone="opportunity"
                              items={opps} totalFinds={n} />
          )}
        </>
      ) : (
        <>
          {(branch.recurring_strengths?.length > 0) && (
            <BranchSubsection title="Recurring Strengths" tone="strength"
                              items={branch.recurring_strengths} totalFinds={n} />
          )}
          {(branch.other_strengths?.length > 0) && (
            <BranchSubsection title="Other Strengths Identified" tone="strength"
                              items={branch.other_strengths} totalFinds={n} />
          )}
          {(branch.recurring_opportunities?.length > 0) && (
            <BranchSubsection title="Recurring Opportunities" tone="opportunity"
                              items={branch.recurring_opportunities} totalFinds={n} />
          )}
          {(branch.other_opportunities?.length > 0) && (
            <BranchSubsection title="Other Opportunities Identified" tone="opportunity"
                              items={branch.other_opportunities} totalFinds={n} />
          )}
          {!(branch.recurring_strengths?.length
             || branch.other_strengths?.length
             || branch.recurring_opportunities?.length
             || branch.other_opportunities?.length) && (
            <p className="text-xs text-[#94A3B8] italic mt-2">
              No satisACTION® strengths or opportunities recorded across these FINDs.
            </p>
          )}
        </>
      )}
    </div>
  );
}


function BranchSubsection({ title, tone, items, totalFinds }) {
  const toneClass = tone === 'strength'
    ? 'text-[#4EBC87]'
    : 'text-[#F97316]';
  return (
    <div className="mt-3">
      <h5 className={`text-[11px] font-semibold uppercase tracking-wider ${toneClass} mb-1`}>
        {title}
      </h5>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="text-xs" data-testid="branch-sat-item">
            <span className="text-[#0F172A] font-medium">{it.phrase}</span>
            <span className="text-[#94A3B8] ml-1">
              — {it.count} of {totalFinds} FIND{totalFinds === 1 ? '' : 's'}
            </span>
            {it.public_ids?.length > 0 && (
              <div className="mt-0.5 flex flex-wrap">
                {it.public_ids.map((pid) => (
                  <ResponseChip key={pid} publicId={pid} />
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}


function formatBranchPeriod(start, end) {
  if (!start && !end) return '';
  const fmt = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.valueOf())) return '';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
  };
  const a = fmt(start), b = fmt(end);
  if (a && b && a === b) return ` · ${a}`;
  if (a && b) return ` · ${a} – ${b}`;
  return a || b ? ` · ${a || b}` : '';
}


/* =============== Ask satisFIND V1 (Client Experience V2) ===============
   Text Q&A grounded in the tenant's authorized FIND data. The backend
   forces the LLM to cite Response IDs only from the retrieved context;
   we render those IDs as clickable deep-links. Insufficient-data path
   is rendered verbatim from the server so we never fabricate content. */
export function AskSatisFindCard({ dateParams, branchIds }) {
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    const q = question.trim();
    if (!q || loading) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const body = {
        question: q,
        date_from: dateParams?.from || null,
        date_to: dateParams?.to || null,
        branch_ids: branchIds || null,
      };
      const r = await fetch(`${API}/satisfind/ask`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setError(j?.detail || `Request failed (${r.status})`);
      } else {
        setResult(await r.json());
      }
    } catch (err) {
      console.error('ask satisfind failed:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-[#E2E8F0] shadow-sm" data-testid="ask-satisfind-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-['Montserrat'] flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#603795]" />
          Ask satisFIND
        </CardTitle>
        <p className="text-xs text-[#64748B]">
          Ask a question about your FINDs. Answers are grounded only in your authorized data and cite the Response IDs behind them.
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="flex gap-2 items-start">
          <textarea
            data-testid="ask-satisfind-input"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder="e.g., Which branches are receiving the most complaints about wait times?"
            rows={2}
            className="flex-1 text-sm rounded-md border border-[#E2E8F0] px-3 py-2
                       focus:outline-none focus:ring-2 focus:ring-[#603795]/30 resize-none"
          />
          <Button
            type="submit"
            data-testid="ask-satisfind-submit"
            disabled={loading || !question.trim()}
            className="bg-[#603795] hover:bg-[#4c2b78] text-white h-auto py-2 px-3"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            <span className="ml-1.5 hidden sm:inline">Ask</span>
          </Button>
        </form>

        {error && (
          <p data-testid="ask-satisfind-error"
             className="mt-3 text-xs text-[#D62B27]">
            {error}
          </p>
        )}

        {result && result.insufficient_data && (
          <div data-testid="ask-satisfind-insufficient"
               className="mt-4 p-3 rounded-md bg-amber-50 border border-amber-200">
            <p className="text-xs font-semibold text-amber-900 uppercase tracking-wider">
              Insufficient data
            </p>
            <p className="text-sm text-amber-900 mt-1">
              {result.message}
            </p>
          </div>
        )}

        {result && !result.insufficient_data && (result.main_finding || result.answer) && (
          <div data-testid="ask-satisfind-answer" className="mt-4 space-y-3">
            {/* Main finding — the direct 1–2 sentence answer. Response
                IDs mentioned inline are auto-linkified so managers can
                jump straight into the source FIND. */}
            <div>
              <p className="text-[11px] font-semibold text-[#603795] uppercase tracking-wider mb-1">
                Main finding
              </p>
              <p className="text-sm text-[#0F172A] leading-relaxed whitespace-pre-wrap"
                 data-testid="ask-satisfind-main">
                {linkifyResponseIds(result.main_finding || result.answer, result.response_ids)}
              </p>
            </div>

            {/* Contributing factors — evidence-based bullets. Rendered
                in a subdued grey so they read as supporting notes,
                not headline claims. */}
            {result.contributing_factors?.length > 0 && (
              <div data-testid="ask-satisfind-contributing">
                <p className="text-[11px] font-semibold text-[#F97316] uppercase tracking-wider mb-1">
                  What is contributing to it
                </p>
                <ul className="list-disc pl-5 space-y-0.5">
                  {result.contributing_factors.map((c, i) => (
                    <li key={i} className="text-sm text-[#334155]">
                      {linkifyResponseIds(c, result.response_ids)}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Needs most attention — the branches the LLM flagged when
                the evidence supports it. Kept as a plain list, no
                extra card wrapper. */}
            {result.needs_attention?.length > 0 && (
              <div data-testid="ask-satisfind-needs-attention">
                <p className="text-[11px] font-semibold text-[#D62B27] uppercase tracking-wider mb-1">
                  Needs most attention
                </p>
                <ul className="list-disc pl-5 space-y-0.5">
                  {result.needs_attention.map((b, i) => (
                    <li key={i} className="text-sm text-[#0F172A] font-medium">{b}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Recommended focus — an actionable next step, framed as
                a suggestion. Only shown when the LLM produced one. */}
            {result.recommended_focus && (
              <div data-testid="ask-satisfind-recommendation">
                <p className="text-[11px] font-semibold text-[#4EBC87] uppercase tracking-wider mb-1">
                  Recommended focus
                </p>
                <p className="text-sm text-[#0F172A] leading-relaxed">
                  {linkifyResponseIds(result.recommended_focus, result.response_ids)}
                </p>
              </div>
            )}

            {/* Sources — every FIND ID is a clickable chip. */}
            {result.response_ids?.length > 0 && (
              <div>
                <p className="text-[11px] font-semibold text-[#64748B] uppercase tracking-wider mb-1">
                  Sources
                </p>
                <div className="flex flex-wrap" data-testid="ask-satisfind-sources">
                  {result.response_ids.map((pid) => (
                    <ResponseChip key={pid} publicId={pid} />
                  ))}
                </div>
              </div>
            )}

            <p className="text-[11px] text-[#94A3B8]">
              Analysis based on {result.analysed_count ?? result.context_size} FIND
              {(result.analysed_count ?? result.context_size) === 1 ? '' : 's'} in your selected scope.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


/**
 * Turn any `RESP-...` / `FIND-...` / matched-public-id token inside
 * a body of text into a clickable link that opens `/reports/{id}`.
 * We only linkify tokens that appear in `validIds` so we never
 * generate broken links for anything the model hallucinated.
 */
function linkifyResponseIds(text, validIds) {
  if (!text) return text;
  const valid = new Set(validIds || []);
  // Detect FIND-* / RESP-* / PID-*-style tokens (alnum, hyphens, uppercase).
  const pattern = /\b([A-Z][A-Z0-9]*-[A-Z0-9-]{3,})\b/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  while ((match = pattern.exec(text)) !== null) {
    const token = match[1];
    parts.push(text.slice(lastIndex, match.index));
    if (valid.has(token)) {
      parts.push(
        <a key={`${token}-${match.index}`}
           href={`/reports/${token}`}
           className="text-[#603795] font-medium underline hover:no-underline"
           data-testid={`ask-satisfind-inline-${token}`}>
          {token}
        </a>
      );
    } else {
      parts.push(token);
    }
    lastIndex = match.index + token.length;
  }
  parts.push(text.slice(lastIndex));
  return parts;
}
